/**
 * Page Content Extractor
 * Extracts visible page content as structured Markdown
 * Generic implementation that works across all pages
 */

/**
 * Check if an element is visible to the user
 */
const isVisible = (element: HTMLElement): boolean => {
  if (!element.offsetParent && element.tagName !== 'BODY') return false;
  
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  
  return true;
};

/**
 * Check if a field contains sensitive information
 */
const isSensitiveField = (element: HTMLElement): boolean => {
  const sensitivePatterns = ['password', 'ssn', 'sin', 'social', 'card', 'cvv', 'account'];
  const text = (element.textContent || '').toLowerCase();
  const ariaLabel = (element.getAttribute('aria-label') || '').toLowerCase();
  const placeholder = (element.getAttribute('placeholder') || '').toLowerCase();
  
  return sensitivePatterns.some(pattern => 
    text.includes(pattern) || ariaLabel.includes(pattern) || placeholder.includes(pattern)
  );
};

/**
 * Get label for an input element
 */
const getInputLabel = (input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string => {
  // Try associated label
  if (input.labels && input.labels.length > 0) {
    return input.labels[0].textContent?.trim() || '';
  }
  
  // Try aria-label
  const ariaLabel = input.getAttribute('aria-label');
  if (ariaLabel) {
    return ariaLabel.trim();
  }

  
  // Try placeholder
  const placeholder = input.getAttribute('placeholder');
  if (placeholder) {
    return placeholder.trim();
  }

  // Try closest label
  const closestLabel = input.closest('label');
  if (closestLabel) {
    return closestLabel.textContent?.trim() || '';
  }
  
  // Try name attribute
  return input.name || '';
};

/**
 * Convert HTML table to Markdown table
 * Preserves structure and shows selected states (●/○ for radio buttons)
 */
const convertTableToMarkdown = (table: HTMLTableElement): string => {
  const rows: string[] = [];
  const tableRows = table.querySelectorAll('tr');
  
  if (tableRows.length === 0) return '';
  
  tableRows.forEach((tr, rowIndex) => {
    const cells: string[] = [];
    const cellElements = tr.querySelectorAll('th, td');
    
    cellElements.forEach(cell => {
      let cellText = '';
      
      // Check for radio buttons or checkboxes
      const radio = cell.querySelector('input[type="radio"]') as HTMLInputElement;
      const checkbox = cell.querySelector('input[type="checkbox"]') as HTMLInputElement;
      
      if (radio) {
        cellText = radio.checked ? '●' : '○';
      } else if (checkbox) {
        cellText = checkbox.checked ? '☑' : '☐';
      } else {
        // Get text content, excluding nested tables
        cellText = Array.from(cell.childNodes)
          .filter(node => node.nodeType === Node.TEXT_NODE || 
                         (node.nodeType === Node.ELEMENT_NODE && 
                          (node as Element).tagName !== 'TABLE'))
          .map(node => node.textContent?.trim())
          .filter(text => text)
          .join(' ');
      }
      
      cells.push(cellText.replace(/\|/g, '\\|').trim() || ' ');
    });
    
    if (cells.length > 0) {
      rows.push(`| ${cells.join(' | ')} |`);
      
      // Add header separator after first row if it contains th elements
      if (rowIndex === 0 && tr.querySelector('th')) {
        rows.push(`| ${cells.map(() => '---').join(' | ')} |`);
      }
    }
  });
  
  return rows.join('\n');
};

/**
 * Convert HTML list to Markdown list
 */
const convertListToMarkdown = (list: Element, output: string[], depth: number): void => {
  const items = list.querySelectorAll(':scope > li');
  const indent = '  '.repeat(depth);
  const isOrdered = list.tagName === 'OL';
  
  items.forEach((item, index) => {
    const prefix = isOrdered ? `${index + 1}.` : '-';
    const text = Array.from(item.childNodes)
      .filter(node => node.nodeType === Node.TEXT_NODE || 
                     (node.nodeType === Node.ELEMENT_NODE && 
                      !['UL', 'OL'].includes((node as Element).tagName)))
      .map(node => node.textContent?.trim())
      .filter(text => text)
      .join(' ');
    
    if (text) {
      output.push(`${indent}${prefix} ${text}`);
    }
    
    // Process nested lists
    const nestedList = item.querySelector(':scope > ul, :scope > ol');
    if (nestedList) {
      convertListToMarkdown(nestedList, output, depth + 1);
    }
  });
};

/**
 * Process benefit option (package) elements
 * These have a specific structure: header with radio + name, body with table
 */
const processBenefitOption = (element: HTMLElement, output: string[]): void => {
  // Get the radio button and package name
  const radio = element.querySelector('input[type="radio"]') as HTMLInputElement;
  const header = element.querySelector('.header');
  
  if (!radio || !header) return;
  
  const packageName = header.textContent?.trim() || 'Unknown Package';
  const isSelected = radio.checked;
  const isDisabled = radio.disabled;
  
  // Selection marker: ● for selected, ○ for unselected
  const selectionMarker = isSelected ? '●' : '○';
  
  // Add package name with selection indicator
  output.push('');
  output.push(`### ${selectionMarker} ${packageName}${isDisabled ? ' (Current Selection)' : ''}`);
  
  // Find and process the table in the benefit-option-body
  const table = element.querySelector('table');
  if (table) {
    const markdown = convertTableToMarkdown(table as HTMLTableElement);
    if (markdown) {
      output.push('');
      output.push(markdown);
    }
  }
  
  // Check for additional text/notes below the table
  const additionalText = element.querySelector('.additionaltext');
  if (additionalText) {
    const text = additionalText.textContent?.trim();
    if (text) {
      output.push('');
      output.push(`> ${text}`);
    }
  }
  
  output.push('');
};

/**
 * Process enrollment summary items
 * These are cards showing selected benefits with cost breakdown
 */
const processEnrollmentItem = (element: HTMLElement, output: string[]): void => {
  const card = element.querySelector('.card');
  if (!card) return;
  
  const cardHeader = card.querySelector('.card-header');
  if (!cardHeader) return;
  
  // Get benefit title and option
  const benefitTitle = cardHeader.querySelector('.benefit-title .h2')?.textContent?.trim();
  const benefitOption = cardHeader.querySelector('.benifit-option .h2')?.textContent?.trim();
  
  if (!benefitTitle) return;
  
  // Format as heading with option
  output.push('');
  if (benefitOption) {
    output.push(`### ${benefitTitle}`);
    output.push(`**${benefitOption}**`);
  } else {
    output.push(`### ${benefitTitle}`);
  }
  
  // Get the value boxes (cost breakdown)
  const valueBoxes = card.querySelector('.valueboxes');
  if (valueBoxes) {
    const values = Array.from(valueBoxes.querySelectorAll('div')).map(div => {
      const text = div.textContent?.trim() || '';
      // Extract label and value (e.g., "Annual Cost$2,376" -> "Annual Cost: $2,376")
      const match = text.match(/^(.+?)(\$[\d,]+\.?\d*)$/);
      if (match) {
        return `- ${match[1].trim()}: ${match[2]}`;
      }
      return text ? `- ${text}` : '';
    }).filter(v => v && v !== '- ');
    
    if (values.length > 0) {
      output.push('');
      output.push(...values);
    }
  }
  
  output.push('');
};

/**
 * Process enrollment summary container
 * Handles the summary page with header, items, and footer totals
 */
const processEnrollmentSummary = (element: HTMLElement, output: string[]): void => {
  // Get the sticky header with column labels
  const header = element.querySelector('.enrollmenthead');
  if (header) {
    const headerCols = Array.from(header.querySelectorAll('.valueboxes > div'))
      .map(col => col.textContent?.trim())
      .filter(text => text);
    
    if (headerCols.length > 0) {
      output.push('');
      output.push(`**Summary Columns:** ${headerCols.join(' | ')}`);
      output.push('');
    }
  }
  
  // Process enrollment items (individual benefits)
  const items = element.querySelectorAll('.enrollmentitem:not(.foottotal)');
  items.forEach(item => {
    processEnrollmentItem(item as HTMLElement, output);
  });
  
  // Process footer totals
  const footerTotal = element.querySelector('.foottotal');
  if (footerTotal) {
    output.push('');
    output.push('---');
    output.push('');
    
    const rows = footerTotal.querySelectorAll('.m-0');
    rows.forEach(row => {
      const title = row.querySelector('.title')?.textContent?.trim();
      if (!title) return;
      
      const valueBoxes = row.querySelector('.valueboxes');
      if (valueBoxes) {
        const values = Array.from(valueBoxes.querySelectorAll('div'))
          .map(div => div.textContent?.trim())
          .filter(text => text && text !== ' ')
          .map(text => {
            if (!text) return '';
            // Extract label and value
            const match = text.match(/^(.+?)(\$[\d,]+\.?\d*)$/);
            if (match) {
              return `${match[1].trim()}: ${match[2]}`;
            }
            return text;
          })
          .filter(text => text);
        
        if (values.length > 0) {
          output.push(`**${title}** ${values.join(' | ')}`);
        } else {
          output.push(`**${title}**`);
        }
      }
    });
    
    output.push('');
  }
};

/**
 * Process a DOM element and convert to Markdown
 * Recursive function that preserves hierarchy
 */
const processElement = (element: Element, output: string[], depth: number): void => {
  const htmlElement = element as HTMLElement;
  
  // Skip invisible elements
  if (!isVisible(htmlElement)) return;
  
  // Skip script, style, and chatbot elements
  const skipTags = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME'];
  if (skipTags.includes(element.tagName) || 
      htmlElement.classList.contains('chatbot-widget') ||
      htmlElement.id === 'chatbot-widget') {
    return;
  }
  
  const tag = element.tagName;
  
  // Handle enrollment summary (summary page with selected benefits)
  if (htmlElement.classList.contains('enrollmentsummary')) {
    processEnrollmentSummary(htmlElement, output);
    return;
  }
  
  // Handle benefit options (packages with radio buttons and tables)
  if (htmlElement.classList.contains('benefit-option')) {
    processBenefitOption(htmlElement, output);
    return;
  }
  
  // Handle headings
  if (/^H[1-6]$/.test(tag)) {
    const level = parseInt(tag[1]);
    const text = element.textContent?.trim();
    if (text) {
      output.push('');
      output.push(`${'#'.repeat(level)} ${text}`);
      output.push('');
    }
    return;
  }
  
  // Handle tables (but skip if inside benefit-option, already handled above)
  if (tag === 'TABLE' && !htmlElement.closest('.benefit-option')) {
    const markdown = convertTableToMarkdown(element as HTMLTableElement);
    if (markdown) {
      output.push('');
      output.push(markdown);
      output.push('');
    }
    return;
  }
  
  // Handle lists
  if (tag === 'UL' || tag === 'OL') {
    output.push('');
    convertListToMarkdown(element, output, 0);
    output.push('');
    return;
  }
  
  // Handle paragraphs
  if (tag === 'P') {
    const text = element.textContent?.trim();
    if (text && text.length > 0) {
      output.push('');
      output.push(text);
      output.push('');
    }
    return;
  }
  
  // Handle buttons
  if (tag === 'BUTTON' || (tag === 'INPUT' && (element as HTMLInputElement).type === 'button')) {
    const text = element.textContent?.trim() || (element as HTMLInputElement).value;
    if (text) {
      output.push(`[${text}]`);
    }
    return;
  }
  
  // Handle form inputs (text, select, textarea)
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') {
    const input = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const inputType = (input as HTMLInputElement).type || 'text';
    
    // Skip hidden and password fields
    if (inputType === 'hidden' || inputType === 'password') return;
    
    // Skip radio and checkbox (handled in tables)
    if (inputType === 'radio' || inputType === 'checkbox') return;
    
    const label = getInputLabel(input);
    const value = (input as HTMLInputElement).value || (input as HTMLSelectElement).value;
    
    if (label) {
      if (value && !isSensitiveField(htmlElement)) {
        output.push(`**${label}:** ${value.substring(0, 50)}`);
      } else {
        output.push(`**${label}:**`);
      }
    }
    return;
  }
  
  // Handle alerts and notifications
  if (htmlElement.classList.contains('alert') || 
      htmlElement.getAttribute('role') === 'alert') {
    const text = element.textContent?.trim();
    if (text) {
      output.push('');
      output.push(`> ${text}`);
      output.push('');
    }
    return;
  }
  
  // Handle divs and sections - process children
  if (tag === 'DIV' || tag === 'SECTION' || tag === 'ARTICLE' || 
      tag === 'MAIN' || tag === 'ASIDE' || tag === 'HEADER' || tag === 'FOOTER') {
    
    // Check if this is a labeled section
    const ariaLabel = htmlElement.getAttribute('aria-label');
    const heading = element.querySelector(':scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6');
    
    if (ariaLabel && !heading) {
      output.push('');
      output.push(`### ${ariaLabel}`);
      output.push('');
    }
    
    // Process children
    Array.from(element.children).forEach(child => {
      processElement(child, output, depth + 1);
    });
    return;
  }
  
  // Handle strong/bold text
  if (tag === 'STRONG' || tag === 'B') {
    const text = element.textContent?.trim();
    if (text) {
      output.push(`**${text}**`);
    }
    return;
  }
  
  // Handle emphasis/italic text
  if (tag === 'EM' || tag === 'I') {
    const text = element.textContent?.trim();
    if (text) {
      output.push(`*${text}*`);
    }
    return;
  }
  
  // For other elements, process children
  if (element.children.length > 0) {
    Array.from(element.children).forEach(child => {
      processElement(child, output, depth + 1);
    });
  }
};

/**
 * Capture page content as Markdown
 * Main entry point for page content extraction
 * 
 * @param containerSelector - CSS selector for the main content container
 * @returns Markdown representation of the page content
 */
export const capturePageAsMarkdown = (
  containerSelector = '.content-holder, main, [role="main"]'
): string => {
  try {
    // Find the main content container
    const container = document.querySelector(containerSelector);
    
    if (!container) {
      console.warn('Page content container not found:', containerSelector);
      return '';
    }
    
    const output: string[] = [];
    
    // Add page title if available
    const pageTitle = document.title;
    if (pageTitle) {
      output.push(`# ${pageTitle}`);
      output.push('');
    }
    
    // Process the container
    processElement(container, output, 0);
    
    // Join and clean up the output
    const markdown = output
      .join('\n')
      .replace(/\n{3,}/g, '\n\n') // Remove excessive newlines
      .trim();
    
    // Limit size to ~2KB (approximately 2000 characters)
    // if (markdown.length > 2000) {
    //   markdown = markdown.substring(0, 2000) + '\n\n[Content truncated...]';
    // }
    
    return markdown;
  } catch (error) {
    console.error('Failed to capture page content:', error);
    return '';
  }
};

/**
 * Get page content size estimate
 * Useful for debugging and monitoring
 */
export const getPageContentSize = (markdown: string): {
  characters: number;
  bytes: number;
  lines: number;
} => {
  const bytes = new Blob([markdown]).size;
  const lines = markdown.split('\n').length;
  
  return {
    characters: markdown.length,
    bytes,
    lines
  };
};
